import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { formatCurrency, formatDate, toISODateString } from '../lib/utils';
import { Plus, Trash2, Pencil, CalendarIcon, Coins, CheckCircle, AlertTriangle, Calculator, Banknote, RefreshCw, Wallet } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ExportButtons } from '../components/ExportButtons';
import { MissingDatesWarning } from '../components/MissingDatesWarning';
import { SnapshotButtons } from '../components/SnapshotButtons';

const BILLETS = [
  { value: 500, color: '#9B59B6', label: '500', textColor: '#fff' },
  { value: 200, color: '#F1C40F', label: '200', textColor: '#333' },
  { value: 100, color: '#27AE60', label: '100', textColor: '#fff' },
  { value: 50, color: '#E67E22', label: '50', textColor: '#fff' },
  { value: 20, color: '#3498DB', label: '20', textColor: '#fff' },
  { value: 10, color: '#E74C3C', label: '10', textColor: '#fff' },
  { value: 5, color: '#95A5A6', label: '5', textColor: '#fff' },
];

const PIECES = [
  { value: 2, color: '#C0C0C0', innerColor: '#FFD700', label: '2 EUR', size: 'large' },
  { value: 1, color: '#FFD700', innerColor: '#C0C0C0', label: '1 EUR', size: 'large' },
  { value: 0.50, color: '#FFD700', label: '50c', size: 'medium' },
  { value: 0.20, color: '#FFD700', label: '20c', size: 'medium' },
  { value: 0.10, color: '#FFD700', label: '10c', size: 'small' },
  { value: 0.05, color: '#CD7F32', label: '5c', size: 'small' },
  { value: 0.02, color: '#CD7F32', label: '2c', size: 'tiny' },
  { value: 0.01, color: '#CD7F32', label: '1c', size: 'tiny' },
];

const BilletVisual = ({ billet, count, onChange }) => (
  <div className="flex items-center gap-3 p-2 rounded-lg bg-card border border-border hover:bg-muted/50 transition-colors">
    <div className="w-16 h-10 rounded-md flex items-center justify-center font-bold text-xs shadow-md relative overflow-hidden"
      style={{ backgroundColor: billet.color, color: billet.textColor }}>
      <span className="relative z-10">{billet.label} EUR</span>
    </div>
    <Input type="number" min="0" value={count || ''} onChange={(e) => onChange(parseInt(e.target.value) || 0)} className="w-16 text-center font-mono" placeholder="0" />
    <div className="w-20 text-right font-mono font-medium tabular-nums text-sm">{formatCurrency(billet.value * (count || 0))}</div>
  </div>
);

const PieceVisual = ({ piece, count, onChange }) => {
  const sizes = { large: 'w-10 h-10 text-xs', medium: 'w-9 h-9 text-[10px]', small: 'w-8 h-8 text-[10px]', tiny: 'w-7 h-7 text-[9px]' };
  return (
    <div className="flex items-center gap-2 p-1.5 rounded-lg bg-card border border-border hover:bg-muted/50 transition-colors">
      <div className={`${sizes[piece.size]} rounded-full flex items-center justify-center font-bold shadow-md`}
        style={{ backgroundColor: piece.color, border: piece.innerColor ? `2px solid ${piece.innerColor}` : 'none' }}>
        <span className="text-gray-800">{piece.label}</span>
      </div>
      <Input type="number" min="0" value={count || ''} onChange={(e) => onChange(parseInt(e.target.value) || 0)} className="w-14 text-center font-mono text-sm" placeholder="0" />
      <div className="w-16 text-right font-mono font-medium tabular-nums text-xs">{formatCurrency(piece.value * (count || 0))}</div>
    </div>
  );
};

export function VerificationEspecesPage() {
  const { api } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [prefillDate, setPrefillDate] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try { const res = await api.get('/verification-especes'); setData(res.data); }
    catch { toast.error('Erreur'); } finally { setLoading(false); }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    try { await api.post('/verification-especes/delete-bulk', { ids: [...selectedIds] }); toast.success(`${selectedIds.size} supprimée(s)`); setSelectedIds(new Set()); fetchData(); }
    catch { toast.error('Erreur'); }
  };

  const toggleSelect = (id) => { const n = new Set(selectedIds); if (n.has(id)) n.delete(id); else n.add(id); setSelectedIds(n); };

  const filteredData = data.filter(d => {
    if (year && d.date && !d.date.startsWith(String(year))) return false;
    if (month && d.date) {
      const m = parseInt(d.date.split('-')[1]);
      if (m !== month) return false;
    }
    if (dateFrom && d.date < dateFrom) return false;
    if (dateTo && d.date > dateTo) return false;
    return true;
  });

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredData.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filteredData.map(d => d.id)));
  };

  const VerificationForm = ({ onClose, defaultDate, editItem }) => {
    const isEdit = !!editItem;
    // Reconstitue billets/pieces depuis montant_enveloppe en éditant n'est pas possible (on a perdu la décomposition).
    // On commence avec un total simplifié dans une seule case "Enveloppe directe" si édition.
    const [date, setDate] = useState(editItem?.date || defaultDate || toISODateString(new Date()));
    const [dateOpen, setDateOpen] = useState(false);
    const [billets, setBillets] = useState({});
    const [pieces, setPieces] = useState({});
    const [enveloppeManual, setEnveloppeManual] = useState(editItem?.montant_enveloppe || 0); // utilisé en mode édition
    const [fondCaisseDepart, setFondCaisseDepart] = useState(editItem?.fond_caisse_depart || 0);
    const [fondCaisseFin, setFondCaisseFin] = useState(editItem?.fond_caisse_fin || 0);
    // Sorties détaillées : achats (matériel/courses) + dons (paiement employé, fournisseur cash, etc.)
    const [achats, setAchats] = useState(editItem?.achats_detail || []);
    const [dons, setDons] = useState(editItem?.dons_detail || []);
    const [saving, setSaving] = useState(false);
    const [caReelInfo, setCaReelInfo] = useState(null);
    const [loadingCA, setLoadingCA] = useState(false);

    const totalAchats = achats.reduce((s, x) => s + (parseFloat(x.montant) || 0), 0);
    const totalDons = dons.reduce((s, x) => s + (parseFloat(x.montant) || 0), 0);

    const addLine = (setter) => setter(prev => [...prev, { description: '', montant: 0, beneficiaire: '', facture: '' }]);
    const updateLine = (setter, i, key, value) => setter(prev => prev.map((x, j) => j === i ? { ...x, [key]: value } : x));
    const removeLine = (setter, i) => setter(prev => prev.filter((_, j) => j !== i));

    const fetchCAForDate = async (d) => {
      setLoadingCA(true);
      try {
        const res = await api.get('/verification-especes/ca-reel-especes', { params: { date: d } });
        setCaReelInfo(res.data);
      } catch { setCaReelInfo(null); }
      finally { setLoadingCA(false); }
    };

    useEffect(() => { if (date) fetchCAForDate(date); }, [date]);

    const totalBillets = BILLETS.reduce((sum, b) => sum + (b.value * (billets[b.value] || 0)), 0);
    const totalPieces = PIECES.reduce((sum, p) => sum + (p.value * (pieces[p.value] || 0)), 0);
    const totalCounted = totalBillets + totalPieces;
    // Si on édite : utiliser enveloppeManual sauf si l'utilisateur recompte (totalCounted > 0)
    const totalEnveloppe = isEdit && totalCounted === 0 ? enveloppeManual : totalCounted;
    const especesEncaissees = totalEnveloppe + fondCaisseFin - fondCaisseDepart + totalAchats + totalDons;
    const caReelEspeces = caReelInfo?.ca_reel_especes || 0;
    const ecart = especesEncaissees - caReelEspeces;
    const isOK = Math.abs(ecart) <= 5;

    const handleSubmit = async () => {
      setSaving(true);
      try {
        const payload = {
          date, mode_paiement: 'Especes', montant_enveloppe: totalEnveloppe,
          fond_caisse_depart: fondCaisseDepart, fond_caisse_fin: fondCaisseFin,
          achats_perso: totalAchats, argent_donne: totalDons,
          achats_detail: achats.filter(x => parseFloat(x.montant) > 0),
          dons_detail: dons.filter(x => parseFloat(x.montant) > 0),
          taux_tva: 0.1
        };
        if (isEdit) {
          await api.put(`/verification-especes/${editItem.id}`, payload);
          toast.success('Vérification modifiée');
        } else {
          await api.post('/verification-especes', payload);
          toast.success('Vérification enregistrée');
        }
        fetchData(); onClose();
      } catch { toast.error('Erreur'); } finally { setSaving(false); }
    };

    return (
      <div className="space-y-5 max-h-[80vh] overflow-y-auto">
        <div className="space-y-2">
          <Label>Date</Label>
          <Popover open={dateOpen} onOpenChange={setDateOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full justify-start text-left font-normal">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {date ? format(new Date(date), 'PPP', { locale: fr }) : 'Choisir'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar mode="single" selected={date ? new Date(date) : undefined}
                onSelect={(d) => { if (d) { setDate(toISODateString(d)); setDateOpen(false); } }} locale={fr} />
            </PopoverContent>
          </Popover>
        </div>

        {/* CA Réel reference */}
        <Card className="border-blue-300 bg-blue-50/50 dark:bg-blue-900/10">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-600 font-medium">CA Réel Espèces du {formatDate(date)}</p>
                <p className="text-2xl font-bold text-blue-700 tabular-nums mt-1">
                  {loadingCA ? <RefreshCw className="w-5 h-5 animate-spin" /> : formatCurrency(caReelEspeces)}
                </p>
                {caReelInfo && <p className="text-xs text-muted-foreground">CA Total jour : {formatCurrency(caReelInfo.ca_reel_total)}</p>}
              </div>
              <Coins className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Separator />

        {isEdit && totalCounted === 0 && (
          <div className="rounded-md bg-blue-50 border border-blue-200 p-3 space-y-2">
            <p className="text-xs text-blue-900 font-semibold">Mode édition — montant enveloppe enregistré : <b>{formatCurrency(enveloppeManual)}</b></p>
            <p className="text-[11px] text-blue-700">Modifie ce montant directement ou recompte les billets/pièces ci-dessous pour l&apos;écraser.</p>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={enveloppeManual || ''}
              onChange={(e) => setEnveloppeManual(parseFloat(e.target.value) || 0)}
              className="h-9 max-w-xs"
              data-testid="edit-enveloppe-manual"
              placeholder="Total enveloppe €"
            />
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold flex items-center gap-2"><Banknote className="w-5 h-5 text-green-600" /> Billets</h3>
            <Badge variant="secondary" className="font-mono">{formatCurrency(totalBillets)}</Badge>
          </div>
          <div className="grid gap-1.5">{BILLETS.map(b => <BilletVisual key={b.value} billet={b} count={billets[b.value]} onChange={(c) => setBillets(p => ({ ...p, [b.value]: c }))} />)}</div>
        </div>

        <Separator />

        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold flex items-center gap-2"><Coins className="w-5 h-5 text-yellow-600" /> Pièces</h3>
            <Badge variant="secondary" className="font-mono">{formatCurrency(totalPieces)}</Badge>
          </div>
          <div className="grid grid-cols-2 gap-1.5">{PIECES.map(p => <PieceVisual key={p.value} piece={p} count={pieces[p.value]} onChange={(c) => setPieces(pr => ({ ...pr, [p.value]: c }))} />)}</div>
        </div>

        <Separator />

        {/* Fond de caisse & Sorties */}
        <div className="space-y-3">
          <h3 className="font-bold flex items-center gap-2"><Wallet className="w-5 h-5 text-indigo-600" /> Fond de caisse & Sorties</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Fond de caisse départ</Label>
              <Input type="number" step="0.01" min="0" value={fondCaisseDepart || ''} placeholder="0.00"
                onChange={(e) => setFondCaisseDepart(parseFloat(e.target.value) || 0)} data-testid="fond-caisse-depart" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Fond de caisse fin</Label>
              <Input type="number" step="0.01" min="0" value={fondCaisseFin || ''} placeholder="0.00"
                onChange={(e) => setFondCaisseFin(parseFloat(e.target.value) || 0)} data-testid="fond-caisse-fin" />
            </div>
          </div>
        </div>

        <Separator />

        {/* Achats payés en espèces */}
        <SortieEspeceList
          title="Achats payés en espèces"
          subtitle="Argent sorti de la caisse pour acheter du matériel, des courses, etc. → s'ajoute aux espèces encaissées (reconstitution du CA)"
          icon="🛒"
          color="text-orange-600"
          items={achats}
          onAdd={() => addLine(setAchats)}
          onChange={(i, k, v) => updateLine(setAchats, i, k, v)}
          onRemove={(i) => removeLine(setAchats, i)}
          total={totalAchats}
          formatCurrency={formatCurrency}
          dataPrefix="achat"
        />

        <Separator />

        {/* Argent donné */}
        <SortieEspeceList
          title="Argent donné / sorti"
          subtitle="Argent donné à un employé, fournisseur, ou prélèvement → s'ajoute aux espèces encaissées"
          icon="💸"
          color="text-amber-600"
          items={dons}
          onAdd={() => addLine(setDons)}
          onChange={(i, k, v) => updateLine(setDons, i, k, v)}
          onRemove={(i) => removeLine(setDons, i)}
          total={totalDons}
          formatCurrency={formatCurrency}
          dataPrefix="don"
        />

        <Separator />

        {/* Résultat comparaison */}
        <div className={`p-5 rounded-xl border-2 ${isOK && totalEnveloppe > 0 ? 'border-green-400 bg-green-50 dark:bg-green-950/20' : totalEnveloppe > 0 ? 'border-red-400 bg-red-50 dark:bg-red-950/20' : 'border-border bg-muted/50'}`}>
          <div className="grid grid-cols-2 gap-3 text-center mb-3">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Enveloppe comptée</p>
              <p className="text-lg font-bold tabular-nums">{formatCurrency(totalEnveloppe)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Fond départ → fin</p>
              <p className="text-lg font-bold tabular-nums">{formatCurrency(fondCaisseDepart)} → {formatCurrency(fondCaisseFin)}</p>
            </div>
          </div>
          {(totalAchats > 0 || totalDons > 0) && (
            <div className="grid grid-cols-2 gap-3 text-center mb-3">
              {totalAchats > 0 && <div><p className="text-xs text-muted-foreground">🛒 Achats espèces ({achats.filter(x => parseFloat(x.montant) > 0).length})</p><p className="font-bold tabular-nums text-orange-600">-{formatCurrency(totalAchats)}</p></div>}
              {totalDons > 0 && <div><p className="text-xs text-muted-foreground">💸 Argent donné ({dons.filter(x => parseFloat(x.montant) > 0).length})</p><p className="font-bold tabular-nums text-orange-600">-{formatCurrency(totalDons)}</p></div>}
            </div>
          )}
          <Separator className="my-2" />
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-xs text-muted-foreground mb-1">CA Réel Espèces</p>
              <p className="text-xl font-bold text-blue-600 tabular-nums">{formatCurrency(caReelEspeces)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Espèces encaissées</p>
              <p className="text-xl font-bold tabular-nums">{formatCurrency(especesEncaissees)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Ecart</p>
              <p className={`text-xl font-bold tabular-nums ${Math.abs(ecart) <= 5 ? 'text-green-600' : 'text-red-600'}`}>
                {ecart > 0 ? '+' : ''}{formatCurrency(ecart)}
              </p>
            </div>
          </div>
          {totalEnveloppe > 0 && (
            <div className="mt-3 pt-3 border-t border-border/50 text-center">
              {isOK ? (
                <Badge className="bg-green-100 text-green-800 gap-1"><CheckCircle className="w-3 h-3" /> Conforme (écart &le; 5 EUR)</Badge>
              ) : (
                <Badge variant="destructive" className="gap-1"><AlertTriangle className="w-3 h-3" /> Non conforme (écart {formatCurrency(Math.abs(ecart))})</Badge>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex gap-2">
          <Button type="button" variant="outline" onClick={() => { setBillets({}); setPieces({}); }}>Réinitialiser</Button>
          <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </DialogFooter>
      </div>
    );
  };

  const okCount = filteredData.filter(d => d.verification === 'OK').length;
  const nokCount = filteredData.filter(d => d.verification === 'NOK').length;

  return (
    <div className="space-y-6">
      <div className="module-header">
        <div>
          <h1 className="module-title">Vérification Espèces</h1>
          <p className="text-muted-foreground mt-1">Comptage de caisse vs CA Réel</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <SnapshotButtons module="verification_especes" moduleLabel="Vérification Espèces" />
          <ExportButtons api={api} moduleKey="verification_especes" />
          <Button className="gap-2 bg-purple-600 hover:bg-purple-700" onClick={() => setDialogOpen(true)}>
            <Plus className="w-4 h-4" /> Nouvelle vérification
          </Button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Total vérifications</CardTitle></CardHeader>
          <CardContent><p className="text-3xl font-bold">{filteredData.length}</p></CardContent>
        </Card>
        <Card className="border-green-300 bg-green-50/50 dark:bg-green-900/10">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-600" /> Conformes</CardTitle></CardHeader>
          <CardContent><p className="text-3xl font-bold text-green-600">{okCount}</p></CardContent>
        </Card>
        <Card className={nokCount > 0 ? 'border-red-300 bg-red-50/50 dark:bg-red-900/10' : ''}>
          <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-600" /> Non conformes</CardTitle></CardHeader>
          <CardContent><p className={`text-3xl font-bold ${nokCount > 0 ? 'text-red-600' : ''}`}>{nokCount}</p></CardContent>
        </Card>
      </div>

      {/* Date filters */}
      <div className="flex flex-wrap items-center gap-4">
        <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
          <SelectTrigger className="w-[100px] h-9" data-testid="filter-year"><SelectValue /></SelectTrigger>
          <SelectContent>
            {[2024, 2025, 2026, 2027].map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={month ? month.toString() : 'all'} onValueChange={(v) => setMonth(v === 'all' ? null : parseInt(v))}>
          <SelectTrigger className="w-[140px] h-9" data-testid="filter-month"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les mois</SelectItem>
            {['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'].map((m, i) => (
              <SelectItem key={i+1} value={(i+1).toString()}>{m}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Label className="text-xs whitespace-nowrap">Du</Label>
          <Input type="date" className="w-[150px] h-9" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="filter-date-from" />
          <Label className="text-xs whitespace-nowrap">Au</Label>
          <Input type="date" className="w-[150px] h-9" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="filter-date-to" />
          {(dateFrom || dateTo) && <Button variant="ghost" size="sm" onClick={() => { setDateFrom(''); setDateTo(''); }}>Effacer</Button>}
        </div>
      </div>

      {/* Bulk delete */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg" data-testid="bulk-delete-bar">
          <span className="text-sm font-medium">{selectedIds.size} sélectionnée(s)</span>
          <Button variant="outline" size="sm" onClick={() => setSelectedIds(new Set())}>Désélectionner</Button>
          <AlertDialog>
            <AlertDialogTrigger asChild><Button variant="destructive" size="sm" className="gap-1" data-testid="bulk-delete-btn"><Trash2 className="w-4 h-4" /> Supprimer</Button></AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader><AlertDialogTitle>Supprimer {selectedIds.size} vérification(s) ?</AlertDialogTitle><AlertDialogDescription>Irréversible.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel><AlertDialogAction onClick={handleDeleteSelected}>Supprimer</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}

      {/* Missing dates warning */}
      <MissingDatesWarning data={filteredData} dateField="date" year={year} month={month || new Date().getMonth() + 1} dateFrom={dateFrom} dateTo={dateTo} onDateClick={(date) => { setPrefillDate(date); setDialogOpen(true); }} />

      {/* Synthèse mensuelle des écarts */}
      {filteredData.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Calculator className="w-5 h-5" /> Synthèse des écarts</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Mois</TableHead>
                  <TableHead className="text-center">Vérifications</TableHead>
                  <TableHead className="text-center">Conformes</TableHead>
                  <TableHead className="text-center">Non conformes</TableHead>
                  <TableHead className="text-right">CA Réel Espèces</TableHead>
                  <TableHead className="text-right">Espèces encaissées</TableHead>
                  <TableHead className="text-right">Écart total</TableHead>
                  <TableHead className="text-right">Écart moyen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(() => {
                  const moisNom = ['', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
                  const byMonth = {};
                  filteredData.forEach(d => {
                    const m = d.date ? parseInt(d.date.split('-')[1]) : 0;
                    if (!byMonth[m]) byMonth[m] = { count: 0, ok: 0, nok: 0, totalEcart: 0, totalCA: 0, totalEnc: 0 };
                    byMonth[m].count++;
                    if (d.verification === 'OK') byMonth[m].ok++; else byMonth[m].nok++;
                    byMonth[m].totalEcart += d.ecart || 0;
                    byMonth[m].totalCA += d.montant_revenu || 0;
                    byMonth[m].totalEnc += d.especes_encaissees || d.montant_enveloppe || 0;
                  });
                  const months = Object.keys(byMonth).map(Number).sort((a, b) => a - b);
                  const totalAll = { count: 0, ok: 0, nok: 0, totalEcart: 0, totalCA: 0, totalEnc: 0 };
                  months.forEach(m => { Object.keys(totalAll).forEach(k => totalAll[k] += byMonth[m][k]); });
                  return (
                    <>
                      {months.map(m => (
                        <TableRow key={m}>
                          <TableCell className="font-medium">{moisNom[m] || m}</TableCell>
                          <TableCell className="text-center"><Badge variant="secondary">{byMonth[m].count}</Badge></TableCell>
                          <TableCell className="text-center"><Badge className="bg-green-100 text-green-800">{byMonth[m].ok}</Badge></TableCell>
                          <TableCell className="text-center">{byMonth[m].nok > 0 ? <Badge variant="destructive">{byMonth[m].nok}</Badge> : <span className="text-muted-foreground">0</span>}</TableCell>
                          <TableCell className="text-right tabular-nums text-blue-600">{formatCurrency(byMonth[m].totalCA)}</TableCell>
                          <TableCell className="text-right tabular-nums">{formatCurrency(byMonth[m].totalEnc)}</TableCell>
                          <TableCell className={`text-right tabular-nums font-bold ${Math.abs(byMonth[m].totalEcart) > 5 ? 'text-red-600' : 'text-green-600'}`}>
                            {byMonth[m].totalEcart > 0 ? '+' : ''}{formatCurrency(byMonth[m].totalEcart)}
                          </TableCell>
                          <TableCell className={`text-right tabular-nums ${Math.abs(byMonth[m].totalEcart / byMonth[m].count) > 5 ? 'text-red-600' : 'text-green-600'}`}>
                            {formatCurrency(byMonth[m].totalEcart / byMonth[m].count)}
                          </TableCell>
                        </TableRow>
                      ))}
                      {months.length > 1 && (
                        <TableRow className="bg-muted/50 font-bold">
                          <TableCell>Total</TableCell>
                          <TableCell className="text-center"><Badge variant="secondary">{totalAll.count}</Badge></TableCell>
                          <TableCell className="text-center"><Badge className="bg-green-100 text-green-800">{totalAll.ok}</Badge></TableCell>
                          <TableCell className="text-center">{totalAll.nok > 0 ? <Badge variant="destructive">{totalAll.nok}</Badge> : '0'}</TableCell>
                          <TableCell className="text-right tabular-nums text-blue-600">{formatCurrency(totalAll.totalCA)}</TableCell>
                          <TableCell className="text-right tabular-nums">{formatCurrency(totalAll.totalEnc)}</TableCell>
                          <TableCell className={`text-right tabular-nums font-bold ${Math.abs(totalAll.totalEcart) > 5 ? 'text-red-600' : 'text-green-600'}`}>
                            {totalAll.totalEcart > 0 ? '+' : ''}{formatCurrency(totalAll.totalEcart)}
                          </TableCell>
                          <TableCell className={`text-right tabular-nums ${Math.abs(totalAll.totalEcart / totalAll.count) > 5 ? 'text-red-600' : 'text-green-600'}`}>
                            {formatCurrency(totalAll.totalEcart / totalAll.count)}
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  );
                })()}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Coins className="w-5 h-5" /> Historique</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]">
                  <input type="checkbox" className="h-4 w-4 rounded border-gray-300 cursor-pointer accent-primary"
                    checked={filteredData.length > 0 && selectedIds.size === filteredData.length} onChange={toggleSelectAll} data-testid="select-all-checkbox" />
                </TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">CA Réel Espèces</TableHead>
                <TableHead className="text-right">Enveloppe</TableHead>
                <TableHead className="text-right">Ecart</TableHead>
                <TableHead className="text-right">Base HT</TableHead>
                <TableHead className="text-right">TVA</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="w-[60px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredData.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">Aucune vérification</TableCell></TableRow>
              ) : (
                filteredData.map(item => (
                  <TableRow key={item.id} className={selectedIds.has(item.id) ? 'bg-primary/5' : ''}>
                    <TableCell>
                      <input type="checkbox" className="h-4 w-4 rounded border-gray-300 cursor-pointer accent-primary"
                        checked={selectedIds.has(item.id)} onChange={() => toggleSelect(item.id)} />
                    </TableCell>
                    <TableCell className="font-medium">{formatDate(item.date)}</TableCell>
                    <TableCell className="text-right tabular-nums text-blue-600">{formatCurrency(item.montant_revenu)}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{formatCurrency(item.montant_enveloppe)}</TableCell>
                    <TableCell className={`text-right tabular-nums font-bold ${Math.abs(item.ecart) > 5 ? 'text-red-600' : 'text-green-600'}`}>
                      {item.ecart > 0 ? '+' : ''}{formatCurrency(item.ecart)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">{formatCurrency(item.base_ht)}</TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">{formatCurrency(item.tva)}</TableCell>
                    <TableCell>
                      {item.verification === 'OK' ? (
                        <Badge className="gap-1 bg-green-100 text-green-800"><CheckCircle className="w-3 h-3" /> OK</Badge>
                      ) : (
                        <Badge variant="destructive" className="gap-1"><AlertTriangle className="w-3 h-3" /> NOK</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => { setEditItem(item); setDialogOpen(true); }}
                        data-testid={`edit-verif-${item.id}`}
                        title="Modifier"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) { setEditItem(null); setPrefillDate(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl"><Coins className="w-6 h-6 text-purple-600" /> {editItem ? 'Modifier' : 'Nouveau'} Comptage de Caisse</DialogTitle>
          </DialogHeader>
          <VerificationForm
            key={editItem?.id || 'new'}
            onClose={() => { setDialogOpen(false); setPrefillDate(null); setEditItem(null); }}
            defaultDate={prefillDate}
            editItem={editItem}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}


function SortieEspeceList({ title, subtitle, icon, color, items, onAdd, onChange, onRemove, total, formatCurrency, dataPrefix }) {
  return (
    <div className="space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div>
          <Label className={`text-sm font-semibold flex items-center gap-1.5 ${color}`}>
            <span>{icon}</span> {title}
          </Label>
          <p className="text-[11px] text-muted-foreground mt-0.5">{subtitle}</p>
        </div>
        <div className="text-right">
          <Button type="button" size="sm" variant="outline" onClick={onAdd} className="gap-1" data-testid={`add-${dataPrefix}`}>
            <Plus className="w-3 h-3" /> Ajouter
          </Button>
          {total > 0 && <p className={`text-sm font-bold tabular-nums mt-1 ${color}`}>{formatCurrency(total)}</p>}
        </div>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground italic px-2">Aucune entrée — cliquez sur &laquo;&nbsp;Ajouter&nbsp;&raquo; pour saisir une sortie d&apos;argent.</p>
      ) : (
        <div className="space-y-1.5">
          {items.map((it, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-end bg-muted/30 p-2 rounded-md" data-testid={`${dataPrefix}-row-${i}`}>
              <div className="col-span-12 sm:col-span-5">
                <Label className="text-[10px] text-muted-foreground">Description (obligatoire)</Label>
                <Input
                  className="h-8"
                  placeholder="Ex : Sacs poubelle, Carrefour"
                  value={it.description}
                  onChange={(e) => onChange(i, 'description', e.target.value)}
                  data-testid={`${dataPrefix}-desc-${i}`}
                />
              </div>
              <div className="col-span-6 sm:col-span-2">
                <Label className="text-[10px] text-muted-foreground">Montant €</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  className="h-8 tabular-nums"
                  placeholder="0.00"
                  value={it.montant || ''}
                  onChange={(e) => onChange(i, 'montant', parseFloat(e.target.value) || 0)}
                  data-testid={`${dataPrefix}-montant-${i}`}
                />
              </div>
              <div className="col-span-6 sm:col-span-2">
                <Label className="text-[10px] text-muted-foreground">Bénéficiaire</Label>
                <Input
                  className="h-8"
                  placeholder="À qui ?"
                  value={it.beneficiaire}
                  onChange={(e) => onChange(i, 'beneficiaire', e.target.value)}
                  data-testid={`${dataPrefix}-benef-${i}`}
                />
              </div>
              <div className="col-span-10 sm:col-span-2">
                <Label className="text-[10px] text-muted-foreground">N° facture</Label>
                <Input
                  className="h-8"
                  placeholder="Réf"
                  value={it.facture}
                  onChange={(e) => onChange(i, 'facture', e.target.value)}
                  data-testid={`${dataPrefix}-facture-${i}`}
                />
              </div>
              <div className="col-span-2 sm:col-span-1 flex justify-end">
                <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => onRemove(i)} data-testid={`${dataPrefix}-del-${i}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
